import { useEffect, useMemo } from 'react'
import { Heading } from '../../Atoms'
import { Grid, Search } from '../../Molecules'
import LeksikonDirectory from '../../Molecules/LeksikonDirectory'
import LeksikonDirectoryWithIcon from '../../Molecules/LeksikonDirectoryWithIcons'
import LeksikonDocument from '../../Molecules/LeksikonDocument'
import LeksikonSearchResult from '../../Molecules/LeksikonSearchResult'
import { LeksikonProps } from './props'
import { useLeksikon } from './hooks'

/**
 * @prop seeds : a json "object tree" with matching nodes, used to build the leksikon "directories" and "documents". For example a translated object from locize.
 * Each node has a slug that is used to navigation between views of the calendar. Each slug needs to start with "/lexicon" in seeds.
 * @prop searchTitle : text that is displayed as result title
 * @prop searchPlaceHolder :t ext that is used as placeholder for the searchbar
 * @prop view : the slug of the view that is displayed
 * @prop setView : a function to set the view (must start with "/leksikon")
 * @returns Leksikon component
 */
const Lexicon = ({
    getHeader,
    hideHeader,
    searchPlaceHolder,
    searchTitle,
    seeds,
    setView,
    view,
}: LeksikonProps) => {
    const { content, onChangeSearch } = useLeksikon(
        searchTitle,
        seeds,
        setView,
        view
    )

    useEffect(() => {
        if (getHeader) getHeader(content.title)
    }, [content.title, getHeader])

    const contentView = useMemo(() => {
        if (
            content.type !== 'Document' &&
            content.searchResults &&
            content.searchResults.searchedText !== ''
        ) {
            return <LeksikonSearchResult {...content.searchResults} />
        } else if (content.type === 'DirectoryWithIcons') {
            return <LeksikonDirectoryWithIcon buttons={content.buttons ?? []} />
        } else if (content.type === 'Document') {
            return (
                <LeksikonDocument
                    breadcrumbs={content.breadcrumbs ?? []}
                    content={content.content}
                    title={content.title}
                    highLightedText={content.highLightedText}
                />
            )
        } else if (content.type === 'Directory') {
            return <LeksikonDirectory content={content.section} />
        }
        throw new Error('This leksikon node is not implemented yet.')
    }, [content])

    return (
        <Grid gap={4}>
            {!hideHeader && content.type !== 'Document' && (
                <Heading level={1} size={4} css={{ mb: '$2' }} primary>
                    {content.title}
                </Heading>
            )}
            <Grid gap={16}>
                {content.type !== 'Document' && (
                    <Search
                        placeholder={searchPlaceHolder ?? 'search'}
                        onChange={onChangeSearch}
                    />
                )}
                {contentView}
            </Grid>
        </Grid>
    )
}

export default Lexicon
